import React, {useEffect, useState} from 'react';
import {Alert, Avatar, Box, Button, Card, CardContent, Grid, Input, TextField, Typography} from "@mui/material";
import {PhotoCamera} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import {axiosApiInstance} from "../AxiosUtils";
import {usePermissions, useTranslate} from "react-admin";
import {formatShortDate} from "../lib/dates";

const ProfilePage = () => {
    const translate = useTranslate();
    const {permissions} = usePermissions();

    // Profile state
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit profile state
    const [givenName, setGivenName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [email, setEmail] = useState('');
    const [isEmailEditable, setEmailEditable] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profileSuccess, setProfileSuccess] = useState(false);
    const [profileError, setProfileError] = useState(null);

    // Avatar state
    const [selectedAvatar, setSelectedAvatar] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const [avatarSuccess, setAvatarSuccess] = useState(false);
    const [avatarError, setAvatarError] = useState(null);

    // Password change state
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [passwordError, setPasswordError] = useState(null);

    // Determine API path based on role
    const getApiPath = () => {
        if (permissions === 'admin') {
            return 'admin';
        } else if (permissions === 'umbrella' || permissions.includes('umbrella')) {
            return 'umbrellad';
        } else {
            return 'charitad';
        }
    };

    // Fetch profile on mount
    useEffect(() => {
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const apiPath = getApiPath();
            const response = await axiosApiInstance.get(
                `${process.env.REACT_APP_API_URL}/api/v1/${apiPath}/profile`
            );
            setProfile(response.data);
            setGivenName(response.data.given_name || '');
            setFamilyName(response.data.family_name || '');
            setEmail(response.data.email || '');
            setLoading(false);
        } catch (error) {
            console.error('Error fetching profile:', error);
            setProfileError(translate('Failed to load profile'));
            setLoading(false);
        }
    };

    const handleAvatarSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedAvatar(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAvatarUpload = async () => {
        if (!selectedAvatar) return;

        setUploadingAvatar(true);
        setAvatarSuccess(false);
        setAvatarError(null);

        try {
            const formData = new FormData();
            formData.append('media', selectedAvatar);

            await axiosApiInstance.post(
                `${process.env.REACT_APP_API_URL}/api/v1/user/avatar`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setAvatarSuccess(true);
            setSelectedAvatar(null);
            setAvatarPreview(null);
            // Refresh profile to get new avatar
            await fetchProfile();
        } catch (error) {
            console.error('Error uploading avatar:', error);
            setAvatarError(translate('Failed to upload avatar'));
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleProfileUpdate = async () => {
        setSaving(true);
        setProfileSuccess(false);
        setProfileError(null);

        try {
            const apiPath = getApiPath();

            // Send as JSON (not FormData) to match backend @RequestBody expectation
            const requestBody = {
                given_name: givenName,
                family_name: familyName,
                email: email,
                enabled: profile?.enabled !== undefined ? profile.enabled : true
            };

            const response = await axiosApiInstance.put(
                `${process.env.REACT_APP_API_URL}/api/v1/${apiPath}/profile`,
                requestBody,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            setProfile(response.data);
            setProfileSuccess(true);
            setEmailEditable(false); // Reset email editable state after successful update
            setTimeout(() => setProfileSuccess(false), 5000);
        } catch (error) {
            console.error('Error updating profile:', error);
            const errorMessage = error.response?.data || translate('Failed to update profile');
            setProfileError(typeof errorMessage === 'string' ? errorMessage : translate('Failed to update profile'));
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async () => {
        if (newPassword !== confirmPassword) {
            setPasswordError(translate('Passwords do not match'));
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError(translate('Password must be at least 6 characters'));
            return;
        }

        setChangingPassword(true);
        setPasswordSuccess(false);
        setPasswordError(null);

        try {
            const formData = new FormData();
            formData.append('new_password', newPassword);

            await axiosApiInstance.post(
                `${process.env.REACT_APP_API_URL}/api/v1/user/setNewPassword`,
                formData
            );

            setPasswordSuccess(true);
            setNewPassword('');
            setConfirmPassword('');
            setTimeout(() => setPasswordSuccess(false), 5000);
        } catch (error) {
            console.error('Error changing password:', error);
            setPasswordError(translate('Failed to change password'));
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{p: 3}}>
                <Typography>{translate('Loading')}...</Typography>
            </Box>
        );
    }

    const currentAvatar = avatarPreview || (profile?.avatar ? `${process.env.REACT_APP_CDN_URL}/${profile.avatar}` : null);

    return (
        <Box sx={{p: 3, maxWidth: 1200, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                {translate('My Profile')}
            </Typography>

            <Grid container spacing={3}>
                {/* Avatar Section */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {translate('Profile Picture')}
                            </Typography>
                            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
                                <Avatar
                                    src={currentAvatar}
                                    sx={{width: 150, height: 150}}
                                />
                                <Input
                                    accept="image/*"
                                    id="avatar-upload"
                                    type="file"
                                    onChange={handleAvatarSelect}
                                    sx={{display: 'none'}}
                                />
                                <label htmlFor="avatar-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        startIcon={<PhotoCamera/>}
                                    >
                                        {translate('Choose Image')}
                                    </Button>
                                </label>
                                {selectedAvatar && (
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        loading={uploadingAvatar}
                                        onClick={handleAvatarUpload}
                                    >
                                        {translate('Upload')}
                                    </LoadingButton>
                                )}
                                {avatarSuccess && (
                                    <Alert severity="success">{translate('Avatar updated successfully')}</Alert>
                                )}
                                {avatarError && (
                                    <Alert severity="error">{avatarError}</Alert>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Profile Information */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {translate('Personal Information')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={translate('First name')}
                                        value={givenName}
                                        onChange={(e) => setGivenName(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={translate('Last name')}
                                        value={familyName}
                                        onChange={(e) => setFamilyName(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
                                    <TextField
                                        fullWidth
                                        label={translate('Email')}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        variant="outlined"
                                        type="email"
                                        disabled={!isEmailEditable}
                                        helperText={
                                            isEmailEditable
                                                ? translate('Warning: Changing email will update your login username. You will need to use the new email to login to both the admin panel and mobile app.')
                                                : translate('Enable editing to change your email address')
                                        }
                                        error={isEmailEditable}
                                    />
                                    <div style={{display: 'flex', alignItems: 'center', paddingTop: '8px'}}>
                                        <label style={{marginRight: '8px', whiteSpace: 'nowrap'}}>
                                            {translate('Make editable')}
                                        </label>
                                        <input
                                            type="checkbox"
                                            checked={isEmailEditable}
                                            onChange={() => setEmailEditable(!isEmailEditable)}
                                        />
                                    </div>
                                </Grid>
                                <Grid item xs={12}>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        loading={saving}
                                        onClick={handleProfileUpdate}
                                    >
                                        {translate('Save Changes')}
                                    </LoadingButton>
                                </Grid>
                                {profileSuccess && (
                                    <Grid item xs={12}>
                                        <Alert severity="success">{translate('Profile updated successfully')}</Alert>
                                    </Grid>
                                )}
                                {profileError && (
                                    <Grid item xs={12}>
                                        <Alert severity="error">{profileError}</Alert>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Read-only info */}
                            <Box sx={{mt: 3}}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    {translate('Role')}: {profile?.realm_role || 'N/A'}
                                </Typography>
                                {profile?.charity_model && (
                                    <Typography variant="subtitle2" color="textSecondary">
                                        {translate('Organization')}: {profile.charity_model.charity_name}
                                    </Typography>
                                )}
                                {profile?.create_date_time && (
                                    <Typography variant="subtitle2" color="textSecondary">
                                        {translate('Registration date')}: {formatShortDate(profile.create_date_time)}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Password Change Section */}
                    <Card sx={{mt: 3}}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {translate('Change Password')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={translate('New Password')}
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={translate('Confirm Password')}
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <LoadingButton
                                        variant="contained"
                                        color="secondary"
                                        loading={changingPassword}
                                        onClick={handlePasswordChange}
                                        disabled={!newPassword || !confirmPassword}
                                    >
                                        {translate('Change Password')}
                                    </LoadingButton>
                                </Grid>
                                {passwordSuccess && (
                                    <Grid item xs={12}>
                                        <Alert severity="success">{translate('Password changed successfully. You will need to use the new password to login to both the admin panel and mobile app.')}</Alert>
                                    </Grid>
                                )}
                                {passwordError && (
                                    <Grid item xs={12}>
                                        <Alert severity="error">{passwordError}</Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfilePage;
